import type { HttpMethod, RequestOptions } from "@/api/client";

export type RequestCacheStorage = "memory" | "session";

export interface RequestCachePolicy {
	enabled: boolean;
	ttlMs: number;
	storage: RequestCacheStorage;
}

const DEFAULT_CACHE_TTL_MS = readEnvInt("VITE_API_CACHE_DEFAULT_TTL_MS", 60_000);
const SITES_CACHE_TTL_MS = readEnvInt("VITE_API_CACHE_SITES_TTL_MS", 3_600_000);
const TOPOLOGY_SITE_CACHE_TTL_MS = readEnvInt(
	"VITE_API_CACHE_TOPOLOGY_SITE_TTL_MS",
	300_000,
);
const INVENTORY_CACHE_TTL_MS = readEnvInt("VITE_API_CACHE_INVENTORY_TTL_MS", 60_000);
const MONITORING_CACHE_TTL_MS = readEnvInt("VITE_API_CACHE_MONITORING_TTL_MS", 120_000);

const ROUTE_CACHE_RULES: Array<{ pattern: RegExp; ttlMs: number; }> = [
	{ pattern: /^\/sites\/?$/i, ttlMs: SITES_CACHE_TTL_MS },
	{ pattern: /^\/topology\/site\/[^/]+(?:\/.*)?$/i, ttlMs: TOPOLOGY_SITE_CACHE_TTL_MS },
	{ pattern: /^\/devices\/?$/i, ttlMs: INVENTORY_CACHE_TTL_MS },
	{
		pattern: /^\/(?:monitor\/(?:alerts\/history|metrics|sites|network-events|licenses)|device\/statuses)\/?$/i,
		ttlMs: MONITORING_CACHE_TTL_MS,
	},
];

function readEnvInt(name: string, fallback: number): number {
	const raw = import.meta.env[name as keyof ImportMetaEnv];
	const n = Number(raw);
	return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
}

function toPathname(path: string): string {
	try {
		const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
		return new URL(path, base).pathname;
	} catch {
		return path;
	}
}

function isTruthyBypass(v: unknown): boolean {
	if (typeof v === "boolean") return v;
	if (typeof v === "number") return v === 1;
	if (typeof v === "string") return ["1", "true", "yes", "on"].includes(v.toLowerCase());
	return false;
}

export function shouldBypassRequestCache(query?: RequestOptions["query"]): boolean {
	return isTruthyBypass(query?.force) || isTruthyBypass(query?.refresh);
}

export function routeCachePolicyFor(method: HttpMethod, path: string): RequestCachePolicy {
	if (method !== "GET") {
		return { enabled: false, ttlMs: 0, storage: "memory" };
	}

	const pathname = toPathname(path);
	for (const rule of ROUTE_CACHE_RULES) {
		if (rule.pattern.test(pathname)) {
			return { enabled: true, ttlMs: rule.ttlMs, storage: "session" };
		}
	}

	return { enabled: true, ttlMs: DEFAULT_CACHE_TTL_MS, storage: "session" };
}
